import { useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import { catalogueFilterState, filterList as list } from '../lib/catalogue-filter-state';

const KEYS = { collection: 'collection', category: 'category', qsCategory: 'category', mounting: 'mounting_type', application: 'application',
  tronixCategory: 'tronix_category_slug', flexCategory: 'flex_category_slug', qsWattageRanges: 'qs_watt_ranges', qsCCTs: 'qs_cct',
  qsIPRatings: 'qs_ip', qsCutoutRanges: 'qs_cutout', qsDimming: 'qs_dimming', families: 'family', colors: 'color', cri: 'cri', beam: 'beam',
  search: 'q', sort: 'sort', page: 'page', perPage: 'per_page', view: 'view' };
const LIST_KEYS = new Set(['qsWattageRanges', 'qsCCTs', 'qsIPRatings', 'qsCutoutRanges', 'families', 'colors']);

// URL is the source of truth: direct links, changes, Back/Forward and route switches
// all use the same state. No reverse-sync effect can overwrite a navigation.
export const useCatalogFilters = (routeCollection = '') => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = useRef(params);
  ref.current = params;
  const state = useMemo(() => catalogueFilterState(params, routeCollection), [params, routeCollection]);
  const debouncedSearch = useDebounce(state.search, 350);
  const write = (key, value) => {
    const next = new URLSearchParams(ref.current);
    if (key === 'category') next.delete('qs_category');
    if (key === 'mounting_type') next.delete('mounting');
    if (key === 'qs_cct') next.delete('cct');
    if (key === 'qs_ip') next.delete('ip_rating');
    if (key === 'color') next.delete('body_finish');
    if (value === '' || value == null || (Array.isArray(value) && !value.length) || (key === 'page' && Number(value) === 1)) next.delete(key);
    else next.set(key, Array.isArray(value) ? value.join('|') : String(value));
    if (!['page', 'view'].includes(key)) next.delete('page');
    if (next.toString() === ref.current.toString()) return;
    ref.current = next;
    setParams(next, { replace: key === 'q' });
  };
  const setters = Object.fromEntries(Object.entries(KEYS).map(([field, key]) => ['set' + field[0].toUpperCase() + field.slice(1), value => {
    const previous = LIST_KEYS.has(field) ? list(ref.current.get(key)) : ref.current.get(key);
    write(key, typeof value === 'function' ? value(previous) : value);
  }]));
  const scopeType = state.collection ? 'collection' : state.category ? 'category' : state.mounting ? 'mounting' : state.application ? 'application' : 'global';
  const chips = [];
  const add = (key, value, label, type = 'filter', removable = true) => value && chips.push({ key, value, label, type, removable });
  add('collection', state.collection, `Collection: ${state.collection}`, 'scope', !routeCollection);
  add('category', state.category, `Category: ${state.category}`, 'scope');
  add('mounting', state.mounting, state.mounting.replaceAll('-', ' '));
  add('application', state.application, `Application: ${state.application}`, 'scope');
  add('tronix_category', state.tronixCategory, state.tronixCategory.replaceAll('-', ' '));
  add('flex_category', state.flexCategory, state.flexCategory.replaceAll('-', ' '));
  for (const [field, key, prefix] of [['qsWattageRanges', 'wattage_range', ''], ['qsCCTs', 'cct', 'CCT '], ['qsIPRatings', 'ip', ''], ['qsCutoutRanges', 'cutout_range', 'Cut-out '], ['families', 'family', 'Family '], ['colors', 'color', 'Finish ']]) {
    state[field].forEach(value => add(key, value, prefix + value, 'quick-spec'));
  }
  add('dimming', state.qsDimming, state.qsDimming === 'dimmable' ? 'Dimmable' : 'Non-Dimmable');
  add('cri', state.cri, `CRI ≥ ${state.cri}`);
  add('beam', state.beam, `Beam ${state.beam}`);
  add('search', state.search, `Search: ${state.search}`);
  const chipField = { collection: 'collection', category: 'category', mounting: 'mounting', application: 'application', tronix_category: 'tronixCategory', flex_category: 'flexCategory',
    wattage_range: 'qsWattageRanges', cct: 'qsCCTs', ip: 'qsIPRatings', cutout_range: 'qsCutoutRanges', dimming: 'qsDimming', family: 'families', color: 'colors', cri: 'cri', beam: 'beam', search: 'search' };
  const removeChip = chip => {
    const field = chipField[chip.key];
    if (field) write(KEYS[field], LIST_KEYS.has(field) ? state[field].filter(v => v !== chip.value) : '');
  };
  const clearFilters = () => {
    const next = new URLSearchParams(ref.current);
    [...new Set(Object.values(KEYS)), 'qs_category', 'mounting', 'cct', 'ip_rating', 'body_finish'].forEach(key => {
      if (!['sort', 'per_page', 'view'].includes(key)) next.delete(key);
    });
    ref.current = next;
    setParams(next);
  };
  return { ...state, ...setters, scopeType, debouncedSearch, filterState: state, activeChips: chips,
    activeFiltersCount: chips.filter(c => c.removable).length, removeChip, clearFilters,
    expandScope: () => navigate('/products/catalog') };
};

export default useCatalogFilters;